import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CheckboxConsent } from '@models/consent.model';

@Component({
  selector: 'app-add-checkbox-consent',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddCheckboxConsentComponent),
      multi: true
    }
  ],
  template: `
    <mat-checkbox 
      [formControl]="checkboxControl"
      [disabled]="disabled">
      <ng-content>{{ label }}</ng-content>
    </mat-checkbox>
  `,
  styles: [`
    :host {
      display: block;
    }
    
    ::ng-deep .mat-checkbox-label a {
      color: #1976d2;
      text-decoration: none;
    }
    
    ::ng-deep .mat-checkbox-label a:hover {
      text-decoration: underline;
    }
  `]
})
export class AddCheckboxConsentComponent implements ControlValueAccessor {
  
  @Input() label: string = 'I agree to the terms and conditions';
  
  // Internal checkbox control
  checkboxControl = new FormControl(false, [Validators.requiredTrue]);
  
  // Internal value - CheckboxConsent type
  private _value: CheckboxConsent | null = null;
  
  // Control value accessor methods
  private _onChange = (value: CheckboxConsent | null) => {};
  private _onTouched = () => {};
  
  // Disabled state
  disabled = false;

  constructor() {
    // Subscribe to checkbox changes
    this.checkboxControl.valueChanges.subscribe(checked => {
      if (checked) {
        const consent = this.createCheckboxConsent();
        this._value = consent;
        this._onChange(consent);
        this._onTouched();
      } else {
        this._value = null;
        this._onChange(null);
      }
    });
  }

  // Helper method to create CheckboxConsent object
  private createCheckboxConsent(): CheckboxConsent {
    return {
      viewedTimestamp: new Date().toISOString(),
      checkedTimestamp: new Date().toISOString(),
      method: 'clickwrap',
      accepted: true
    };
  }

  writeValue(value: CheckboxConsent | null): void {
    this._value = value;
    
    // Update the checkbox based on the consent value
    if (value && typeof value === 'object' && 'accepted' in value) {
      this.checkboxControl.setValue(value.accepted, { emitEvent: false });
    } else {
      this.checkboxControl.setValue(false, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: CheckboxConsent | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.checkboxControl.disable();
    } else {
      this.checkboxControl.enable();
    }
  }
}
